/**
 * Which motion columns the timeline shows (W2-04a motion-columns; DESIGN.md §2.4, §4.5, §4.7, §5.6.3, §5.6.4).
 *
 * Instance ids follow the contracted format `motion:<line>:<prop>:<layer>` (W2-04b's curve editor expands a column
 * by setting `timeline.expandedColumn` to one of them). A column has one lane, or two for the Guided pairs:
 *
 *  - Guided: Position as two linked sub-columns X | Y under one header (id of X), Rotation, Opacity, Scroll speed
 *    (not on UI movers unless set), kind-specific columns (Text line: Text, Size W | H, Colour; image / GIF / UI
 *    mover: Size W | H, Colour) and every other property in use (disclosure never hides data; Full-only ones carry
 *    an "in use" badge). A summed property with more than one layer shows its Σ curve and a "uses N layers" chip;
 *    edits go to layer 0 (the base layer).
 *  - Full: Position X, Position Y, Rotation, Opacity, Scroll speed on the ACTIVE layer (editor `activeLayer`; the
 *    layer chips switch it), the kind-specific properties, and every other property in use, each in its own column.
 *  - Pinned columns (Pin column, Compare lines) follow, in pin order, skipping any already shown.
 * Nothing is shown while `timeline.showMotionColumns` is off; the column W2-04b expands is left out (the curve
 * editor replaces it).
 */

import { kindOfLine, isUiMover } from '../../core/model/lineKind';
import { guidedPairOf, isImageLikeLine } from '../../render/timeline/motion/pairs';
import { LAYERED_PROPS } from '../../core/model/props';
import type { ChartDoc, DocIndexes, LineId, LineKind, LineProp, TrackId } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import type { ColumnRef, EditorStoreState, ExperienceLevel } from '../../state/types';

export type SubLabel = 'X' | 'Y' | 'W' | 'H';

export interface LaneSpec {
  readonly prop: LineProp;
  readonly layer: number;
  readonly sub: SubLabel | null;
}

export interface ColumnSpec {
  /** Host instance id: `motion:<line>:<prop>:<layer>` of the first lane. */
  readonly id: string;
  readonly line: LineId;
  readonly lanes: readonly LaneSpec[];
  /** Pinned from another line or by "Pin column" (header shows the line name, unpin button). */
  readonly pinned: boolean;
  readonly level: ExperienceLevel;
  readonly kind: LineKind;
  readonly uiMover: boolean;
  /** Guided view of a layered property: the Σ curve, layer-0 keys, a "uses N layers" chip. */
  readonly sumView: boolean;
  /** Layers in use of the (first lane's) property; 0 for absent tracks and extended properties. */
  readonly layersInUse: readonly number[];
  /** A property the Guided level does not offer, shown because the line uses it. */
  readonly inUseBadge: boolean;
  /** Pair columns: Position X | Y (linked per line) or Size W | H. */
  readonly pair: 'position' | 'size' | null;
}

export const COLUMN_PREFIX = 'motion:';

/** The contracted instance id of a (line, prop, layer) column. */
export function columnIdOf(ref: ColumnRef): string {
  return `${COLUMN_PREFIX}${String(ref.line)}:${ref.prop}:${String(ref.layer)}`;
}

const PROP_SET: ReadonlySet<string> = new Set<LineProp>([
  'moveX',
  'moveY',
  'rotate',
  'alpha',
  'speed',
  'scaleX',
  'scaleY',
  'color',
  'text',
  'incline',
  'paint',
  'gif',
]);

/** Parses `motion:<line>:<prop>:<layer>`; null for anything else. */
export function parseColumnId(id: string): ColumnRef | null {
  if (!id.startsWith(COLUMN_PREFIX)) return null;
  const parts = id.slice(COLUMN_PREFIX.length).split(':');
  if (parts.length !== 3) return null;
  const [l, p, k] = parts as [string, string, string];
  const line = Number(l);
  const layer = Number(k);
  if (!Number.isSafeInteger(line) || !Number.isSafeInteger(layer) || layer < 0 || !PROP_SET.has(p)) return null;
  return { line: line as LineId, prop: p as LineProp, layer };
}

export function sameRef(a: ColumnRef | null, b: ColumnRef | null): boolean {
  return a !== null && b !== null && a.line === b.line && a.prop === b.prop && a.layer === b.layer;
}

export function isLayered(p: LineProp): boolean {
  return (LAYERED_PROPS as readonly string[]).includes(p);
}

/**
 * The other position axis of a column when the line's Position X and Y are linked (the core mirrors key inserts,
 * moves, deletes and curves there), else null.
 */
export function xyPartnerRef(doc: Pick<ChartDoc, 'lines'>, ref: ColumnRef): ColumnRef | null {
  if (ref.prop !== 'moveX' && ref.prop !== 'moveY') return null;
  if (doc.lines.get(ref.line)?.editor.xyLinked !== true) return null;
  return { line: ref.line, prop: ref.prop === 'moveX' ? 'moveY' : 'moveX', layer: ref.layer };
}

/**
 * True when the column's track, or its linked X/Y partner's, has a key strictly between beats `lo` and `hi` (f64):
 * an RPE R…R segment cannot be placed over it (EVT-04; the placement ghost turns red).
 */
export function keysInsideSpan(
  doc: Pick<ChartDoc, 'lines' | 'keys'>,
  idx: Pick<DocIndexes, 'track' | 'keysByTrack'>,
  ref: ColumnRef,
  lo: number,
  hi: number,
): boolean {
  const partner = xyPartnerRef(doc, ref);
  for (const r of partner === null ? [ref] : [ref, partner]) {
    const track = idx.track(r.line, r.prop, r.layer);
    if (track === undefined) continue;
    for (const id of idx.keysByTrack(track)) {
      const k = doc.keys.get(id);
      if (k === undefined) continue;
      const b = Beats.toF64(k.t);
      if (b > lo + 1e-9 && b < hi - 1e-9) return true;
    }
  }
  return false;
}

/** Layer indices of a property's tracks on a line (ascending). */
export function layersOf(doc: ChartDoc, idx: DocIndexes, line: LineId, prop: LineProp): number[] {
  const ids: readonly TrackId[] = idx.tracksOfLine(line).get(prop) ?? [];
  const out: number[] = [];
  for (const id of ids) {
    const t = doc.tracks.get(id);
    if (t?.owner.kind === 'line') out.push(t.owner.layer);
  }
  return out.sort((a, b) => a - b);
}

/** Properties the Guided level offers on a line kind (the rest appear only when in use, with a badge). */
function guidedOffers(prop: LineProp, kind: LineKind, uiMover: boolean): boolean {
  switch (prop) {
    case 'moveX':
    case 'moveY':
    case 'rotate':
    case 'alpha':
    case 'speed':
    case 'color':
      return true;
    case 'scaleX':
      return true;
    case 'scaleY':
      return kind !== 'normal' || uiMover;
    case 'text':
      return kind === 'text';
    default:
      return false;
  }
}

export interface ColumnInputs {
  readonly doc: ChartDoc;
  readonly idx: DocIndexes;
  readonly editor: Pick<EditorStoreState, 'activeLine' | 'activeLayer' | 'pinnedColumns' | 'timeline'>;
  readonly level: ExperienceLevel;
}

/** Order of columns left to right. */
const ORDER: readonly LineProp[] = [
  'moveX',
  'moveY',
  'rotate',
  'alpha',
  'speed',
  'text',
  'scaleX',
  'scaleY',
  'color',
  'incline',
  'paint',
  'gif',
];

function hasTrack(idx: DocIndexes, line: LineId, prop: LineProp): boolean {
  return (idx.tracksOfLine(line).get(prop)?.length ?? 0) > 0;
}

/** Columns of one line (the active line). */
export function lineColumns(inp: ColumnInputs, line: LineId): ColumnSpec[] {
  const { doc, idx, level } = inp;
  const rec = doc.lines.get(line);
  if (rec === undefined) return [];
  const kind = kindOfLine(doc, idx, rec);
  const uiMover = isUiMover(rec);
  const guided = level === 'guided';
  const imageLike = isImageLikeLine(kind, uiMover);
  const wanted = new Set<LineProp>();
  // Base set.
  wanted.add('moveX');
  wanted.add('moveY');
  wanted.add('rotate');
  wanted.add('alpha');
  if (!uiMover || hasTrack(idx, line, 'speed')) wanted.add('speed');
  // Kind-specific columns.
  if (kind === 'text') wanted.add('text');
  if (imageLike) {
    wanted.add('scaleX');
    wanted.add('scaleY');
    wanted.add('color');
  }
  if (kind === 'gif') wanted.add('gif');
  if (kind === 'paint') wanted.add('paint');
  // Everything in use.
  for (const p of ORDER) if (hasTrack(idx, line, p)) wanted.add(p);

  const out: ColumnSpec[] = [];
  const base = { line, pinned: false, level, kind, uiMover } as const;
  const mk = (
    lanes: LaneSpec[],
    extra: Partial<Pick<ColumnSpec, 'sumView' | 'layersInUse' | 'inUseBadge' | 'pair'>> = {},
  ): ColumnSpec => {
    const first = lanes[0] as LaneSpec;
    return {
      ...base,
      id: columnIdOf({ line, prop: first.prop, layer: first.layer }),
      lanes,
      sumView: extra.sumView ?? false,
      layersInUse: extra.layersInUse ?? [],
      inUseBadge: extra.inUseBadge ?? false,
      pair: extra.pair ?? null,
    };
  };

  for (const prop of ORDER) {
    if (!wanted.has(prop)) continue;
    const layered = isLayered(prop);
    const layers = layered ? layersOf(doc, idx, line, prop) : [];
    if (guided) {
      // The Guided pairs (src/render/timeline/motion/pairs.ts, shared with the curve editor): one column each,
      // headed by its first property.
      const pair = guidedPairOf(prop, kind, uiMover);
      if (pair !== null && pair.props[0] !== prop) continue; // the second lane of a pair
      if (pair?.kind === 'position') {
        const lx = layersOf(doc, idx, line, 'moveX');
        const ly = layersOf(doc, idx, line, 'moveY');
        const multi = lx.some((l) => l !== 0) || ly.some((l) => l !== 0);
        out.push(
          mk(
            [
              { prop: 'moveX', layer: 0, sub: 'X' },
              { prop: 'moveY', layer: 0, sub: 'Y' },
            ],
            { pair: 'position', sumView: multi, layersInUse: [...new Set([...lx, ...ly])].sort((a, b) => a - b) },
          ),
        );
        continue;
      }
      if (pair?.kind === 'size') {
        out.push(
          mk(
            [
              { prop: 'scaleX', layer: 0, sub: 'W' },
              { prop: 'scaleY', layer: 0, sub: 'H' },
            ],
            { pair: 'size' },
          ),
        );
        continue;
      }
      const multi = layers.some((l) => l !== 0);
      out.push(
        mk([{ prop, layer: 0, sub: null }], {
          sumView: layered && multi,
          layersInUse: layers,
          inUseBadge: !guidedOffers(prop, kind, uiMover),
        }),
      );
      continue;
    }
    const layer = layered ? Math.max(0, inp.editor.activeLayer) : 0;
    out.push(mk([{ prop, layer, sub: null }], { layersInUse: layers }));
  }
  return out;
}

/** Every motion column to show now, in order (active line, then pinned columns). */
export function computeColumns(inp: ColumnInputs): ColumnSpec[] {
  if (!inp.editor.timeline.showMotionColumns) return [];
  const out: ColumnSpec[] = [];
  const active = inp.editor.activeLine;
  if (active !== null && inp.doc.lines.has(active)) out.push(...lineColumns(inp, active));
  const covered = new Set<string>();
  for (const c of out) for (const l of c.lanes) covered.add(columnIdOf({ line: c.line, prop: l.prop, layer: l.layer }));
  for (const ref of inp.editor.pinnedColumns) {
    const rec = inp.doc.lines.get(ref.line);
    if (rec === undefined) continue;
    const id = columnIdOf(ref);
    if (covered.has(id)) continue;
    covered.add(id);
    const layers = isLayered(ref.prop) ? layersOf(inp.doc, inp.idx, ref.line, ref.prop) : [];
    out.push({
      id,
      line: ref.line,
      lanes: [{ prop: ref.prop, layer: ref.layer, sub: null }],
      pinned: true,
      level: inp.level,
      kind: kindOfLine(inp.doc, inp.idx, rec),
      uiMover: isUiMover(rec),
      sumView: false,
      layersInUse: layers,
      inUseBadge: false,
      pair: null,
    });
  }
  const expanded = inp.editor.timeline.expandedColumn;
  if (expanded === null) return out;
  const exp = parseColumnId(expanded);
  return out.filter(
    (c) =>
      c.id !== expanded &&
      !(exp !== null && c.line === exp.line && c.lanes.some((l) => l.prop === exp.prop && l.layer === exp.layer)),
  );
}

/** A cheap signature of the column set (relayout only when it changes). */
export function columnsSignature(specs: readonly ColumnSpec[]): string {
  return specs
    .map(
      (s) =>
        `${s.id}|${s.lanes.map((l) => `${l.prop}.${String(l.layer)}`).join(',')}|${s.sumView ? 1 : 0}|${s.layersInUse.join('.')}|${s.pinned ? 1 : 0}|${s.kind}`,
    )
    .join(';');
}

/** The lane a ColumnRef points at in a list of columns (column index and lane index), or null. */
export function findLane(
  specs: readonly ColumnSpec[],
  ref: ColumnRef,
): { readonly column: ColumnSpec; readonly lane: number } | null {
  for (const c of specs) {
    if (c.line !== ref.line) continue;
    const i = c.lanes.findIndex(
      (l) => l.prop === ref.prop && (l.layer === ref.layer || (c.sumView && ref.layer === 0)),
    );
    if (i >= 0) return { column: c, lane: i };
  }
  return null;
}
